import React, { useState } from 'react';

import { Box, Button, Center, Flex, Loader, ScrollArea, Space, Tabs, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconSearch } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { AxiosError, HttpStatusCode } from 'axios';
import { YoutubeChannelComponent } from 'components/BatchResourceSelector/YoutubeVideoSelector/YoutubeChannel';
import { YoutubePlaylistComponent } from 'components/BatchResourceSelector/YoutubeVideoSelector/YoutubePlaylist';
import { YoutubeVideoComponent } from 'components/BatchResourceSelector/YoutubeVideoSelector/YoutubeVideo';
import { TabPanelWrapper } from 'components/TabPanelWrapper';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { YoutubeService } from 'services/youtube-service';
import { RECOMMENDATION_TABS } from 'shared/constants/youtube-recommendations';
import {
  RecommendationTab,
  YoutubeChannel,
  YoutubeItemType,
  YoutubePlaylist,
  YoutubeSearchResult,
  YoutubeVideo
} from 'shared/interfaces/Youtube';
import { requiredInputValidator, youtubeVideoUrlValidator } from 'shared/utils/form-validators';
import { extractYoutbeVideoId } from 'shared/utils/youtube';

interface YoutubeVideoSelectorProps {
  isVideoSelected: (videoId: string) => boolean;
  onVideoSelect: (youtubeVideo: YoutubeVideo) => void;
}

export function YoutubeVideoSelector({ isVideoSelected, onVideoSelect }: YoutubeVideoSelectorProps) {
  const [youtubeSearchItems, setYoutubeSearchItems] = useState<(YoutubeVideo | YoutubeChannel | YoutubePlaylist)[]>([]);
  const [youtubeSearchItemIds, setYoutubeSearchItemIds] = useState<Set<string>>(new Set());
  const [lastSearchResult, setLastSearchResult] = useState<YoutubeSearchResult>();
  const [activeRecommendationsTab, setActiveRecommendationsTab] = useState<RecommendationTab>(RECOMMENDATION_TABS[0]);
  const { t } = useTranslation();

  const searchMutation = useMutation({
    mutationFn: YoutubeService.search,
    onSuccess: (searchResult: YoutubeSearchResult) => {
      setLastSearchResult(searchResult);
      const filteredItems = searchResult.items.filter((item) => !youtubeSearchItemIds.has(item.id));
      setYoutubeSearchItems((currentYoutubeSearchItems) => [...currentYoutubeSearchItems, ...filteredItems]);
      setYoutubeSearchItemIds(
        (currentYoutubeSearchItemIds) =>
          new Set([...currentYoutubeSearchItemIds, ...filteredItems.map((item) => item.id)])
      );
    },
    onError: (error: AxiosError) => {
      if (error.response?.status !== HttpStatusCode.TooManyRequests)
        NotificationService.showResult(false, 'FEED_BACK.YOUTUBE.SEARCH.FAILURE');
    }
  });

  const getVideoByIdMutation = useMutation({
    mutationFn: YoutubeService.getVideoById,
    onSuccess: (youtubeVideo: YoutubeVideo) => {
      if (!youtubeVideo.captionsAvailable) {
        searchQueryForm.setErrors({ url: t('FORM_VALIDATION.YOUTUBE_NO_CAPTIONS') });
        return;
      }
      searchQueryForm.reset();
      if (isVideoSelected(youtubeVideo.id)) return;
      onVideoSelect(youtubeVideo);
    },
    onError: () => searchQueryForm.setErrors({ url: t('FORM_VALIDATION.YOUTUBE_VIDEO_URL') })
  });

  const search = () => {
    const validation = searchQueryForm.validate();
    if (validation.hasErrors) return;
    setYoutubeSearchItems([]);
    setLastSearchResult(undefined);
    setYoutubeSearchItemIds(new Set());
    searchMutation.mutate({ query: searchQueryForm.values.query });
  };

  const loadMore = () => {
    if (!lastSearchResult?.nextPageToken) return;
    searchMutation.mutate({
      query: lastSearchResult.searchQuery,
      nextPageToken: lastSearchResult.nextPageToken
    });
  };

  const isValidYoutubeUrl = (maybeYoutubeUrl: string) => youtubeVideoUrlValidator(maybeYoutubeUrl) === null;

  const addVideoFromUrl = (youtubeUrl: string) => {
    const videoId = extractYoutbeVideoId(youtubeUrl);
    if (!videoId) return searchQueryForm.setErrors({ query: t('FORM_VALIDATION.YOUTUBE_VIDEO_URL') });
    getVideoByIdMutation.mutate(videoId);
  };

  const searchQueryForm = useForm({
    initialValues: { query: '' },
    validate: {
      query: (value) => requiredInputValidator(value)
    }
  });

  const processSearch = () => {
    const searchQuery = searchQueryForm.values.query.trim();
    if (isValidYoutubeUrl(searchQuery)) {
      addVideoFromUrl(searchQuery);
    } else {
      search();
    }
  };

  return (
    <Flex w="100%" h="100%" px={20} pt={70} pb={10} direction="column" align="center" justify="start">
      <Flex w="100%">
        <Box w="100%">
          <TextInput
            w="100%"
            placeholder={t('BATCH_RESOURCE_SELECTOR.YOUTUBE.SEARCH_PLACEHOLDER')}
            onKeyDown={(event) => {
              if (event.key === 'Enter') processSearch();
            }}
            {...searchQueryForm.getInputProps('query')}
          />
        </Box>
        <Space w={10} />
        <Button disabled={!searchQueryForm.values.query.trim()} onClick={processSearch}>
          {getVideoByIdMutation.isLoading ? <Loader size="sm" color="gray" /> : <IconSearch />}
        </Button>
      </Flex>
      {youtubeSearchItems.length === 0 && searchMutation.isLoading && (
        <Center w="100%" h="100%">
          <Loader variant="dots" />
        </Center>
      )}
      {youtubeSearchItems.length === 0 && !searchMutation.isLoading && (
        <Tabs
          inverted
          h="100%"
          w="100%"
          value={activeRecommendationsTab.tabName}
          onTabChange={(newTabName) => {
            setActiveRecommendationsTab(
              RECOMMENDATION_TABS.find((tab) => tab.tabName === newTabName) ?? RECOMMENDATION_TABS[0]
            );
          }}
          defaultChecked>
          <Tabs.List grow mt="lg">
            {RECOMMENDATION_TABS.map((tab) => (
              <Tabs.Tab key={tab.tabName} value={tab.tabName}>
                {t(`BATCH_RESOURCE_SELECTOR.YOUTUBE.${tab.recommendationType}`)}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {RECOMMENDATION_TABS.map((tab) => {
            return (
              <Tabs.Panel key={tab.tabName} value={tab.tabName} w="100%">
                <TabPanelWrapper>
                  {activeRecommendationsTab.tabName === tab.tabName &&
                    activeRecommendationsTab.videos.map((video, index) => {
                      return (
                        <YoutubeVideoComponent
                          mt={index === 0 ? 0 : 10}
                          mb={index === activeRecommendationsTab.videos.length - 1 ? 0 : 10}
                          key={video.id}
                          onVideoSelect={onVideoSelect}
                          isSelected={isVideoSelected(video.id)}
                          video={video}
                        />
                      );
                    })}
                </TabPanelWrapper>
              </Tabs.Panel>
            );
          })}
        </Tabs>
      )}
      <ScrollArea
        offsetScrollbars
        mt={10}
        w="100%"
        h="100%"
        sx={{
          transition: 'height 0.5s ease'
        }}>
        {youtubeSearchItems.map((item, index: number) => {
          const mt = index === 0 ? 0 : 10;
          const mb = index === youtubeSearchItems.length - 1 ? 0 : 10;
          if (item.itemType === YoutubeItemType.VIDEO) {
            return (
              <YoutubeVideoComponent
                key={index}
                mt={mt}
                mb={mb}
                onVideoSelect={onVideoSelect}
                isSelected={isVideoSelected(item.id)}
                video={item as YoutubeVideo}
              />
            );
          } else if (item.itemType === YoutubeItemType.CHANNEL) {
            return (
              <YoutubeChannelComponent
                key={index}
                mt={mt}
                mb={mb}
                onVideoSelect={onVideoSelect}
                channel={item as YoutubeChannel}
                isVideoSelected={isVideoSelected}
              />
            );
          } else if (item.itemType === YoutubeItemType.PLAYLIST) {
            return (
              <YoutubePlaylistComponent
                key={index}
                mt={mt}
                mb={mb}
                isVideoSelected={isVideoSelected}
                onVideoSelect={onVideoSelect}
                playlist={item as YoutubePlaylist}
              />
            );
          }
        })}

        {youtubeSearchItems.length !== 0 && (
          <Center mt={10} mb={10}>
            <Button loading={searchMutation.isLoading} onClick={() => loadMore()}>
              {t('APP.ACTIONS.LOAD_MORE')}
            </Button>
          </Center>
        )}
      </ScrollArea>
    </Flex>
  );
}
